import { Component, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';

import { CognosService } from '../../Services/cognos.service';
import { AuthService } from '../../Services/auth.service';

import { Cognos, CognosResponse } from '../../Models/Cognos';
import { DynamicFieldRequest } from '../../Models/FieldEntry';

import { COGNOS_DISPLAYED_COLUMNS } from '../../Constants/cognos-table-columns';
import {
  FIELD_OPTIONS,
  OPERATOR_OPTIONS,
  groupOptions,
  actualityOptions,
  periodOptions
} from '../../Constants/search-options';

@Component({
  selector: 'app-cognos-search',
  templateUrl: './cognos-search.component.html',
  styleUrls: ['./cognos-search.component.css']
})
export class CognosSearchComponent implements OnInit, AfterViewInit {

  // UI State
  labelText = 'Cognos Controller';
  isLoading = false;
  hasRecords = false;
  showOperator = false;

  // Table Setup
  cognos = new MatTableDataSource<Cognos>([]);
  displayedColumns: string[] = COGNOS_DISPLAYED_COLUMNS;

  // Multi-select (ready if you add checkboxes)
  selection = new SelectionModel<Cognos>(true, []);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // Search Form
  form: FormGroup;

  fieldOptions = FIELD_OPTIONS;
  operatorOptions = OPERATOR_OPTIONS;
  groupOptions = groupOptions;
  actualityOptions = actualityOptions;
  periodOptions = periodOptions;

  constructor(
    private readonly fb: FormBuilder,
    private readonly cognosService: CognosService,
    private readonly authService: AuthService
  ) {
    // Initialize dynamic form with one default row
    this.form = this.fb.group({
      fields: this.fb.array([
        this.createRow('', 'File name')
      ])
    });
  }
  
  // Lifecycle
  ngOnInit(): void {
     const account = this.authService.activeAccount;

  if (!account) {
    this.authService.login();
    }
    this.isLoading = true;

    setTimeout(() => {
      this.isLoading = false;
    }, 500); // 1 second delay
  }

  ngAfterViewInit(): void {
    this.cognos.paginator = this.paginator;
    this.cognos.sort = this.sort;
  }

  
  // Form Helpers
  get fields(): FormArray {
    return this.form.get('fields') as FormArray;
  }

  private createRow(operator: '' | 'AND' | 'OR', selectedField: string = ''): FormGroup {
    const group = this.fb.group({
      logicalOperator: [operator],
      selectedField: [selectedField, Validators.required],
      operator: ['contains', Validators.required],
      fieldValue: [''],
      showButtons: [true]
    });

    // Reset value when field changes
    group.get('selectedField')?.valueChanges.subscribe(() => {
      group.patchValue({ fieldValue: '' }, { emitEvent: false });
    });

    return group;
  }

  addFieldWithOperator(op: 'AND' | 'OR', idx: number) {
    const arr = this.fields;

    const insertAt = op === 'AND'
      ? arr.length
      : (() => {
        let i = idx + 1;
        while (i < arr.length && arr.at(i).value.logicalOperator === 'OR') {
          i++;
        }
        return i;
      })();

    arr.insert(insertAt, this.createRow(op));

    if (op === 'OR') {
      this.showOperator = true;
      const prev = arr.at(insertAt - 1).value;

      arr.at(insertAt).patchValue({
        selectedField: prev.selectedField,
        operator: prev.operator,
        showButtons: false
      });
    }
  }

  removeField(idx: number) {
    this.fields.removeAt(idx);

    if (this.fields.length === 0) {
      this.form.setControl('fields', this.fb.array([this.createRow('')]));
    }
  }

  isLastRow(index: number): boolean {
    return index === this.fields.length - 1;
  }

  // Search
  search() {
    this.selection.clear();
    this.cognos.data = [];
    this.hasRecords = false;
    this.isLoading = true;

    const model: DynamicFieldRequest = this.form.value;
    this.searchCognosFiles(model);
  }

  private searchCognosFiles(model: DynamicFieldRequest) {

    this.cognosService.getCognosFiles(model).subscribe({
      next: (response: CognosResponse) => {

        this.cognos.data = response?.fileDetails || [];
        this.hasRecords = this.cognos.data.length === 0;

        // Reset paginator to first page
        if (this.paginator) {
          this.paginator.firstPage();
        }

        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching Cognos Files', err);
        this.isLoading = false;
      }
    });
  }

  download(file: any) {
    // If multiple selected → bulk download
    if (this.selection.selected.length > 0) {
      this.downloadSelected();
    } else {
      // If nothing selected → download clicked row
      this.downloadFilesSequentially([file]);
    }
  }

  // 🔹 Bulk download handler
  downloadSelected() {
    const files = [...this.selection.selected];
    this.selection.clear();
    this.downloadFilesSequentially(files);
  }

  // Sequential download 
  private downloadFilesSequentially(files: any[], index: number = 0) {
    if (!files || index >= files.length) {
      this.isLoading = false;
      return;
    }

    const file = files[index];

    if (!file?.fileName) {
      this.downloadFilesSequentially(files, index + 1);
      return;
    }

    this.isLoading = true;

    this.cognosService.downloadFile(file.fileName).subscribe({
      next: (blob: Blob) => {
        this.triggerDownload(blob, file.fileName);
        this.downloadFilesSequentially(files, index + 1);
      },
      error: (err) => {
        console.error('Download failed for:', file.fileName, err);
        this.downloadFilesSequentially(files, index + 1); // continue
      }
    });
  }

  // Reusable download helper
  private triggerDownload(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();

    window.URL.revokeObjectURL(url);
  }

  // Selection helpers
  isAllSelected(): boolean {
    return this.selection.selected.length === this.cognos.data.length;
  }

  toggleAllRows(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.selection.select(...this.cognos.data);
  }

  // Handles key down events for a table row (e.g., toggles selection on Enter)
  onKeyDown(event: KeyboardEvent, row: any): void {
    console.log('Key Down:', event.key);

    // Example: Toggle on Enter key
    if (event.key === 'Enter') {
      this.selection.toggle(row);
    }
  }

  // Handles key up events for a table row
  onKeyUp(event: KeyboardEvent, row: any): void {
    console.log('Key Up:', event.key);
  }
}
